// frontend/src/pages/LogManagement.js
import React, { useEffect, useState } from 'react';
import api from '../api';
import { Modal, Button, Form, Table, Spinner, Tabs, Tab, Badge, Card, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';

const LogManagement = () => {
  // 标签页状态
  const [activeTab, setActiveTab] = useState('operation');

  // 操作日志状态
  const [operationLogs, setOperationLogs] = useState([]);
  const [operationLoading, setOperationLoading] = useState(true);
  const [operationCurrentPage, setOperationCurrentPage] = useState(1);
  const [operationTotalPages, setOperationTotalPages] = useState(1);
  const [operationTotalCount, setOperationTotalCount] = useState(0);
  const [operationSearchTerm, setOperationSearchTerm] = useState('');

  // 系统日志状态
  const [systemLogs, setSystemLogs] = useState([]);
  const [systemLoading, setSystemLoading] = useState(true);
  const [systemCurrentPage, setSystemCurrentPage] = useState(1);
  const [systemTotalPages, setSystemTotalPages] = useState(1);
  const [systemTotalCount, setSystemTotalCount] = useState(0);
  const [systemSearchTerm, setSystemSearchTerm] = useState('');
  const [systemTypeFilter, setSystemTypeFilter] = useState('all');
  const [systemLevelFilter, setSystemLevelFilter] = useState('all');
  const [systemStartDate, setSystemStartDate] = useState('');
  const [systemEndDate, setSystemEndDate] = useState('');

  // 统计信息
  const [statistics, setStatistics] = useState(null);

  // 删除确认模态框
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('operation'); // 'operation' 或 'system'

  // 清空所有日志确认模态框
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [showClearSystemModal, setShowClearSystemModal] = useState(false);

  const limit = 20;

  useEffect(() => {
    if (activeTab === 'operation') {
      fetchOperationLogs();
    } else if (activeTab === 'system') {
      fetchSystemLogs();
      fetchStatistics();
    }
  }, [operationCurrentPage, systemCurrentPage, activeTab]);

  // 获取操作日志列表
  const fetchOperationLogs = async (search = '') => {
    try {
      setOperationLoading(true);
      const params = new URLSearchParams({
        page: operationCurrentPage,
        limit: limit,
      });
      
      if (search) {
        params.append('search', search);
      }

      const res = await api.get(`/api/admin/logs?${params}`);
      setOperationLogs(res.data.logs);
      setOperationTotalPages(res.data.totalPages);
      setOperationTotalCount(res.data.totalCount);
    } catch (err) {
      console.error('获取操作日志列表失败:', err);
      toast.error(err.response?.data?.message || '获取操作日志列表失败');
    } finally {
      setOperationLoading(false);
    }
  };

  // 获取系统日志列表
  const fetchSystemLogs = async () => {
    try {
      setSystemLoading(true);
      const params = new URLSearchParams({
        page: systemCurrentPage,
        limit: limit,
      });
      
      if (systemSearchTerm) params.append('search', systemSearchTerm);
      if (systemTypeFilter !== 'all') params.append('type', systemTypeFilter);
      if (systemLevelFilter !== 'all') params.append('level', systemLevelFilter);
      if (systemStartDate) params.append('startDate', systemStartDate);
      if (systemEndDate) params.append('endDate', systemEndDate);

      const res = await api.get(`/api/admin/system-logs?${params}`);
      setSystemLogs(res.data.logs);
      setSystemTotalPages(res.data.totalPages);
      setSystemTotalCount(res.data.totalCount);
    } catch (err) {
      console.error('获取系统日志列表失败:', err);
      toast.error(err.response?.data?.message || '获取系统日志列表失败');
    } finally {
      setSystemLoading(false);
    }
  };

  // 获取统计信息
  const fetchStatistics = async () => {
    try {
      const res = await api.get('/api/admin/system-logs/statistics');
      setStatistics(res.data);
    } catch (err) {
      console.error('获取统计信息失败:', err);
    }
  };

  // 操作日志搜索功能
  const handleOperationSearch = async (e) => {
    e.preventDefault();
    setOperationCurrentPage(1);
    await fetchOperationLogs(operationSearchTerm.trim());
  };

  const handleOperationResetSearch = async () => {
    setOperationSearchTerm('');
    setOperationCurrentPage(1);
    await fetchOperationLogs('');
  };

  // 系统日志搜索功能
  const handleSystemSearch = async (e) => {
    e.preventDefault();
    setSystemCurrentPage(1);
    await fetchSystemLogs();
  };

  const handleSystemResetSearch = async () => {
    setSystemSearchTerm('');
    setSystemTypeFilter('all');
    setSystemLevelFilter('all');
    setSystemStartDate('');
    setSystemEndDate('');
    setSystemCurrentPage(1);
    await fetchSystemLogs();
  };

  // 删除单个日志
  const handleDeleteLog = (log, type) => {
    setLogToDelete(log);
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const confirmDeleteLog = async () => {
    try {
      const endpoint = deleteType === 'operation' 
        ? `/api/admin/logs/${logToDelete._id}`
        : `/api/admin/system-logs/${logToDelete._id}`;
      
      await api.delete(endpoint);
      toast.success('日志删除成功');
      setShowDeleteModal(false);
      setLogToDelete(null);
      
      if (deleteType === 'operation') {
        await fetchOperationLogs(operationSearchTerm.trim());
      } else {
        await fetchSystemLogs();
      }
    } catch (err) {
      console.error('删除日志失败:', err);
      toast.error(err.response?.data?.message || '删除日志失败');
    }
  };

  // 清空操作日志
  const handleClearOperationLogs = () => {
    setShowClearAllModal(true);
  };

  const confirmClearOperationLogs = async () => {
    try {
      const res = await api.delete('/api/admin/logs');
      toast.success(`成功清空 ${res.data.deletedCount} 条操作日志`);
      setShowClearAllModal(false);
      setOperationCurrentPage(1);
      await fetchOperationLogs('');
    } catch (err) {
      console.error('清空操作日志失败:', err);
      toast.error(err.response?.data?.message || '清空操作日志失败');
    }
  };

  // 清空系统日志
  const handleClearSystemLogs = () => {
    setShowClearSystemModal(true);
  };

  const confirmClearSystemLogs = async () => {
    try {
      const res = await api.delete('/api/admin/system-logs');
      toast.success(`成功清空 ${res.data.deletedCount} 条系统日志`);
      setShowClearSystemModal(false);
      setSystemCurrentPage(1);
      await fetchSystemLogs();
    } catch (err) {
      console.error('清空系统日志失败:', err);
      toast.error(err.response?.data?.message || '清空系统日志失败');
    }
  };

  // 导出系统日志
  const handleExportLogs = async (format = 'json') => {
    try {
      const params = new URLSearchParams({ format });
      if (systemTypeFilter !== 'all') params.append('type', systemTypeFilter);
      if (systemLevelFilter !== 'all') params.append('level', systemLevelFilter);
      if (systemStartDate) params.append('startDate', systemStartDate);
      if (systemEndDate) params.append('endDate', systemEndDate);

      const response = await api.get(`/api/admin/system-logs/export?${params}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `system-logs-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('日志导出成功');
    } catch (err) {
      console.error('导出日志失败:', err);
      toast.error(err.response?.data?.message || '导出日志失败');
    }
  };

  // 格式化时间
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // 获取日志级别样式
  const getLevelBadgeVariant = (level) => {
    switch (level) {
      case 'error':
      case 'fatal':
        return 'danger';
      case 'warn':
        return 'warning';
      case 'info':
        return 'primary';
      case 'debug':
        return 'secondary';
      case 'trace':
        return 'light';
      default:
        return 'secondary';
    }
  };

  // 获取日志类型样式
  const getTypeBadgeVariant = (type) => {
    switch (type) {
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      case 'auth':
      case 'security':
        return 'info';
      case 'api_request':
        return 'success';
      case 'database':
        return 'primary';
      case 'system':
        return 'secondary';
      case 'socket':
        return 'dark';
      case 'performance':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // 系统日志类型选项
  const logTypes = [
    { value: 'all', label: '全部类型' },
    { value: 'api_request', label: 'API请求' },
    { value: 'error', label: '错误日志' },
    { value: 'warning', label: '警告日志' },
    { value: 'auth', label: '认证相关' },
    { value: 'database', label: '数据库操作' },
    { value: 'socket', label: 'Socket连接' },
    { value: 'system', label: '系统事件' },
    { value: 'security', label: '安全事件' },
    { value: 'performance', label: '性能监控' }
  ];

  // 日志级别选项
  const logLevels = [
    { value: 'all', label: '全部级别' },
    { value: 'trace', label: 'TRACE' },
    { value: 'debug', label: 'DEBUG' },
    { value: 'info', label: 'INFO' },
    { value: 'warn', label: 'WARN' },
    { value: 'error', label: 'ERROR' },
    { value: 'fatal', label: 'FATAL' }
  ];

  // 分页组件
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, margin: '20px 0' }}>
        <Button
          variant="outline-primary"
          size="sm"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
        >
          上一页
        </Button>
        {pages.map(page => (
          <Button
            key={page}
            variant={page === currentPage ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        <Button
          variant="outline-primary"
          size="sm"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
        >
          下一页
        </Button>
      </div>
    );
  };

  // 操作日志分页
  const handleOperationPrevPage = () => {
    if (operationCurrentPage > 1) {
      setOperationCurrentPage(operationCurrentPage - 1);
    }
  };

  const handleOperationNextPage = () => {
    if (operationCurrentPage < operationTotalPages) {
      setOperationCurrentPage(operationCurrentPage + 1);
    }
  };

  // 系统日志分页
  const handleSystemPrevPage = () => {
    if (systemCurrentPage > 1) {
      setSystemCurrentPage(systemCurrentPage - 1);
    }
  };

  const handleSystemNextPage = () => {
    if (systemCurrentPage < systemTotalPages) {
      setSystemCurrentPage(systemCurrentPage + 1);
    }
  };

  // 格式化操作类型
  const formatOperationType = (type) => {
    const types = {
      'points_adjustment': '积分调整',
      'silver_coins_adjustment': '银币调整',
      'user_management': '用户管理',
      'system_maintenance': '系统维护',
      'other': '其他操作'
    };
    return types[type] || type;
  };

  // 格式化状态
  const formatStatus = (status) => {
    return status === 'success' ? '成功' : '失败';
  };

  // 操作日志表格
  const OperationLogsTable = () => (
    <div>
      {/* 搜索栏和操作按钮 */}
      <div style={{ 
        display: 'flex', 
        gap: 10, 
        margin: '0 auto 28px', 
        maxWidth: 1000, 
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 12px #0cf2',
        padding: 18,
        alignItems: 'center',
        animation: 'fadeInUp 0.8s',
        justifyContent: 'space-between'
      }}>
        <Form onSubmit={handleOperationSearch} style={{ display: 'flex', gap: 10, flex: 1 }}>
          <Form.Control
            type="text"
            placeholder="搜索操作类型、详情或状态"
            value={operationSearchTerm}
            onChange={e => setOperationSearchTerm(e.target.value)}
            style={{
              borderRadius: 8,
              border: '1.5px solid #0cf',
              boxShadow: '0 1px 6px #0cf2',
              fontSize: 16,
              flex: 1
            }}
          />
          <Button 
            variant="primary" 
            type="submit" 
            style={{
              borderRadius: 8,
              background: 'linear-gradient(90deg,#0cf,#0ff)',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px #0cf8',
              border: 'none',
              transition: 'background 0.3s'
            }}
          >
            搜索
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleOperationResetSearch} 
            style={{ borderRadius: 8, fontWeight: 'bold' }}
          >
            重置
          </Button>
        </Form>
        
        <Button 
          variant="danger" 
          onClick={handleClearOperationLogs}
          style={{ 
            borderRadius: 8, 
            fontWeight: 'bold',
            marginLeft: 10
          }}
        >
          清空所有日志
        </Button>
      </div>

      {/* 统计信息 */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 20, 
        color: '#0cf', 
        fontSize: 16, 
        fontWeight: 'bold' 
      }}>
        总计 {operationTotalCount} 条日志，当前第 {operationCurrentPage} / {operationTotalPages} 页
      </div>

      {/* 加载状态 */}
      {operationLoading ? (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Spinner animation="border" variant="info" style={{ width: 60, height: 60 }} />
          <div style={{ color: '#0cf', marginTop: 16, fontSize: 18 }}>加载中...</div>
        </div>
      ) : operationLogs.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>暂无操作日志</p>
      ) : (
        <div style={{ margin: '0 auto', maxWidth: '95%', padding: '0 16px' }}>
          <Table 
            bordered 
            hover 
            responsive
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 4px 16px #0cf2',
              animation: 'fadeIn 0.7s',
              background: '#fff'
            }}
          >
            <thead style={{ background: '#e0f7fa' }}>
              <tr>
                <th>ID</th>
                <th>操作者</th>
                <th>目标用户</th>
                <th>操作类型</th>
                <th>积分</th>
                <th>银币</th>
                <th>状态</th>
                <th>详情</th>
                <th>时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {operationLogs.map(log => (
                <tr key={log._id} style={{ background: '#fff' }}>
                  <td>{log._id.slice(-6)}</td>
                  <td>
                    {log.operatorId ? (
                      `${log.operatorId.username} (${log.operatorId.role})`
                    ) : (
                      '系统'
                    )}
                  </td>
                  <td>
                    {log.targetUserId ? log.targetUserId.username : '-'}
                  </td>
                  <td>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: 4, 
                      fontSize: 12,
                      background: '#f0f8ff',
                      color: '#0cf'
                    }}>
                      {formatOperationType(log.operationType)}
                    </span>
                  </td>
                  <td>{log.points || '-'}</td>
                  <td>{log.silverCoins || '-'}</td>
                  <td>
                    <span style={{ 
                      padding: '2px 8px', 
                      borderRadius: 4, 
                      fontSize: 12,
                      background: log.status === 'success' ? '#d4edda' : '#f8d7da',
                      color: log.status === 'success' ? '#155724' : '#721c24'
                    }}>
                      {formatStatus(log.status)}
                    </span>
                  </td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.details || '-'}
                  </td>
                  <td>{formatTime(log.createdAt)}</td>
                  <td>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => handleDeleteLog(log, 'operation')}
                      style={{ borderRadius: 4 }}
                    >
                      删除
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* 分页控制 */}
          <Pagination
            currentPage={operationCurrentPage}
            totalPages={operationTotalPages}
            onPageChange={setOperationCurrentPage}
          />
        </div>
      )}
    </div>
  );

  // 系统日志表格
  const SystemLogsTable = () => (
    <div>
      {/* 高级搜索栏 */}
      <div style={{ 
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 12px #0cf2',
        padding: 18,
        marginBottom: 20,
        animation: 'fadeInUp 0.8s'
      }}>
        <Form onSubmit={handleSystemSearch}>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>搜索关键词</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="搜索消息、用户、IP等"
                  value={systemSearchTerm}
                  onChange={e => setSystemSearchTerm(e.target.value)}
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #0cf',
                    boxShadow: '0 1px 6px #0cf2'
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>日志类型</Form.Label>
                <Form.Select
                  value={systemTypeFilter}
                  onChange={e => setSystemTypeFilter(e.target.value)}
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #0cf'
                  }}
                >
                  {logTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>日志级别</Form.Label>
                <Form.Select
                  value={systemLevelFilter}
                  onChange={e => setSystemLevelFilter(e.target.value)}
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #0cf'
                  }}
                >
                  {logLevels.map(level => (
                    <option key={level.value} value={level.value}>{level.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>开始日期</Form.Label>
                <Form.Control
                  type="date"
                  value={systemStartDate}
                  onChange={e => setSystemStartDate(e.target.value)}
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #0cf'
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>结束日期</Form.Label>
                <Form.Control
                  type="date"
                  value={systemEndDate}
                  onChange={e => setSystemEndDate(e.target.value)}
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #0cf'
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group>
                <Form.Label>&nbsp;</Form.Label>
                <div>
                  <Button 
                    variant="primary" 
                    type="submit"
                    style={{
                      borderRadius: 8,
                      background: 'linear-gradient(90deg,#0cf,#0ff)',
                      fontWeight: 'bold',
                      border: 'none',
                      width: '100%'
                    }}
                  >
                    搜索
                  </Button>
                </div>
              </Form.Group>
            </Col>
          </Row>
          <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
            <Button 
              variant="secondary" 
              onClick={handleSystemResetSearch}
              style={{ borderRadius: 8 }}
            >
              重置
            </Button>
            <Button 
              variant="danger" 
              onClick={handleClearSystemLogs}
              style={{ borderRadius: 8 }}
            >
              清空系统日志
            </Button>
            <Button 
              variant="success" 
              onClick={() => handleExportLogs('json')}
              style={{ borderRadius: 8 }}
            >
              导出JSON
            </Button>
            <Button 
              variant="info" 
              onClick={() => handleExportLogs('csv')}
              style={{ borderRadius: 8 }}
            >
              导出CSV
            </Button>
          </div>
        </Form>
      </div>

      {/* 统计信息卡片 */}
      {statistics && (
        <Row style={{ marginBottom: 20 }}>
          <Col md={3}>
            <Card style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 12 }}>
              <Card.Body style={{ textAlign: 'center' }}>
                <h4>{statistics.totalLogs}</h4>
                <p>总日志数</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white', borderRadius: 12 }}>
              <Card.Body style={{ textAlign: 'center' }}>
                <h4>{statistics.todayLogs}</h4>
                <p>今日日志</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white', borderRadius: 12 }}>
              <Card.Body style={{ textAlign: 'center' }}>
                <h4>{statistics.errorLogs}</h4>
                <p>错误日志</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white', borderRadius: 12 }}>
              <Card.Body style={{ textAlign: 'center' }}>
                <h4>{statistics.apiRequests}</h4>
                <p>API请求</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* 统计信息 */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 20, 
        color: '#0cf', 
        fontSize: 16, 
        fontWeight: 'bold' 
      }}>
        总计 {systemTotalCount} 条日志，当前第 {systemCurrentPage} / {systemTotalPages} 页
      </div>

      {/* 加载状态 */}
      {systemLoading ? (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <Spinner animation="border" variant="info" style={{ width: 60, height: 60 }} />
          <div style={{ color: '#0cf', marginTop: 16, fontSize: 18 }}>加载中...</div>
        </div>
      ) : systemLogs.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#888', marginTop: 40 }}>暂无系统日志</p>
      ) : (
        <div style={{ margin: '0 auto', maxWidth: '95%', padding: '0 16px' }}>
          <Table 
            bordered 
            hover 
            responsive
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              boxShadow: '0 4px 16px #0cf2',
              animation: 'fadeIn 0.7s',
              background: '#fff'
            }}
          >
            <thead style={{ background: '#e0f7fa' }}>
              <tr>
                <th>ID</th>
                <th>类型</th>
                <th>级别</th>
                <th>消息</th>
                <th>用户</th>
                <th>IP地址</th>
                <th>用户代理</th>
                <th>URL</th>
                <th>方法</th>
                <th>状态码</th>
                <th>时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {systemLogs.map(log => (
                <tr key={log._id} style={{ background: '#fff' }}>
                  <td>{log._id?.slice(-6)}</td>
                  <td>
                    <Badge variant={getTypeBadgeVariant(log.type)}>
                      {logTypes.find(t => t.value === log.type)?.label || log.type}
                    </Badge>
                  </td>
                  <td>
                    <Badge variant={getLevelBadgeVariant(log.level)}>
                      {log.level?.toUpperCase()}
                    </Badge>
                  </td>
                  <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.message}
                  </td>
                  <td>{log.username || log.userId?.username || '-'}</td>
                  <td>{log.ip || '-'}</td>
                  <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.userAgent || log.userAgent || '-'}
                  </td>
                  <td style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.url || '-'}
                  </td>
                  <td>{log.method || '-'}</td>
                  <td>{log.statusCode || '-'}</td>
                  <td>{formatTime(log.timestamp || log.createdAt)}</td>
                  <td>
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => handleDeleteLog(log, 'system')}
                      style={{ borderRadius: 4 }}
                    >
                      删除
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* 分页控制 */}
          <Pagination
            currentPage={systemCurrentPage}
            totalPages={systemTotalPages}
            onPageChange={setSystemCurrentPage}
          />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ 
      padding: '32px 0', 
      background: 'linear-gradient(120deg,#f8fcff 60%,#e0f7fa 100%)', 
      minHeight: '100vh', 
      animation: 'fadeIn 1s', 
      paddingTop: 128 
    }}>
      <h3 style={{
        color: '#0cf',
        fontWeight: 900,
        letterSpacing: 2,
        textAlign: 'center',
        marginBottom: 28,
        textShadow: '0 2px 8px #0cf8'
      }}>
        日志管理
      </h3>

      {/* 选项卡 */}
      <div style={{ maxWidth: '95%', margin: '0 auto', padding: '0 16px' }}>
        <Tabs
          activeKey={activeTab}
          onSelect={(tab) => setActiveTab(tab)}
          className="mb-3"
          style={{
            background: '#fff',
            borderRadius: '12px 12px 0 0',
            boxShadow: '0 2px 12px #0cf2',
            padding: '10px'
          }}
        >
          <Tab 
            eventKey="operation" 
            title={
              <span style={{ color: activeTab === 'operation' ? '#0cf' : '#666', fontWeight: 'bold' }}>
                操作日志 {operationTotalCount > 0 && <Badge variant="primary">{operationTotalCount}</Badge>}
              </span>
            }
          >
            <div style={{ background: '#fff', padding: '20px', borderRadius: '0 0 12px 12px' }}>
              <OperationLogsTable />
            </div>
          </Tab>
          <Tab 
            eventKey="system" 
            title={
              <span style={{ color: activeTab === 'system' ? '#0cf' : '#666', fontWeight: 'bold' }}>
                系统日志 {systemTotalCount > 0 && <Badge variant="info">{systemTotalCount}</Badge>}
              </span>
            }
          >
            <div style={{ background: '#fff', padding: '20px', borderRadius: '0 0 12px 12px' }}>
              <SystemLogsTable />
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* 删除单个日志确认模态框 */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>确认删除</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logToDelete && (
            <p>
              确定要删除这条{deleteType === 'operation' ? '操作' : '系统'}日志吗？
              <br />
              <strong>时间：</strong>{formatTime(logToDelete.createdAt || logToDelete.timestamp)}
              {deleteType === 'operation' && (
                <>
                  <br />
                  <strong>操作类型：</strong>{formatOperationType(logToDelete.operationType)}
                </>
              )}
              {deleteType === 'system' && (
                <>
                  <br />
                  <strong>类型：</strong>{logToDelete.type}
                  <br />
                  <strong>消息：</strong>{logToDelete.message}
                </>
              )}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            取消
          </Button>
          <Button variant="danger" onClick={confirmDeleteLog}>
            确认删除
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 清空操作日志确认模态框 */}
      <Modal show={showClearAllModal} onHide={() => setShowClearAllModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>确认清空操作日志</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            ⚠️ 警告：此操作将删除所有操作日志记录，且无法恢复！
          </p>
          <p>
            当前共有 <strong>{operationTotalCount}</strong> 条操作日志，确定要全部清空吗？
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearAllModal(false)}>
            取消
          </Button>
          <Button variant="danger" onClick={confirmClearOperationLogs}>
            确认清空
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 清空系统日志确认模态框 */}
      <Modal show={showClearSystemModal} onHide={() => setShowClearSystemModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>确认清空系统日志</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            ⚠️ 警告：此操作将删除所有系统日志记录，且无法恢复！
          </p>
          <p>
            当前共有 <strong>{systemTotalCount}</strong> 条系统日志，确定要全部清空吗？
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowClearSystemModal(false)}>
            取消
          </Button>
          <Button variant="danger" onClick={confirmClearSystemLogs}>
            确认清空
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes fadeInUp { 
          from { opacity:0; transform:translateY(40px);} 
          to {opacity:1; transform:translateY(0);} 
        }
        @keyframes fadeIn { 
          from { opacity:0;} 
          to {opacity:1;} 
        }
        .nav-tabs .nav-link {
          border-radius: 8px !important;
          margin-right: 8px !important;
          transition: all 0.3s !important;
        }
        .nav-tabs .nav-link.active {
          background: linear-gradient(90deg,#0cf,#0ff) !important;
          color: white !important;
          border-color: #0cf !important;
        }
        .nav-tabs .nav-link:hover {
          border-color: #0cf !important;
        }
      `}</style>
    </div>
  );
};

export default LogManagement;
